import { createContext, useContext, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, X } from "lucide-react";
import { BUDGETS, PROJECT_TYPES } from "../data/content";
import { CONTACT_EMAIL } from "../config";
import { EASE } from "./Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const InquiryContext = createContext(() => {});
export const useInquiry = () => useContext(InquiryContext);

const EMPTY = { name: "", email: "", company: "", project_type: PROJECT_TYPES[0], budget: "", message: "" };

export function InquiryForm({ idPrefix = "inquiry" }) {
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/inquiries`, form);
      setSent(true);
    } catch {
      setError(`We couldn't send that just now. Please email ${CONTACT_EMAIL} instead.`);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="success-state" data-testid={`${idPrefix}-success`}>
        <Check size={32} />
        <p className="eyebrow">Received loud and clear</p>
        <h3>We&apos;ll be in touch.</h3>
        <p>Your brief is with the studio. Expect an email from us within two working days.</p>
      </div>
    );
  }

  return (
    <form className="lv-form" onSubmit={submit} data-testid={`${idPrefix}-form`}>
      {error && (
        <p className="form-error" data-testid={`${idPrefix}-error-message`}>
          {error}
        </p>
      )}
      <div className="form-row">
        <label>
          Name
          <input required value={form.name} onChange={set("name")} data-testid={`${idPrefix}-name-input`} />
        </label>
        <label>
          Email
          <input required type="email" value={form.email} onChange={set("email")} data-testid={`${idPrefix}-email-input`} />
        </label>
      </div>
      <div className="form-row">
        <label>
          Company / Brand
          <input value={form.company} onChange={set("company")} data-testid={`${idPrefix}-company-input`} />
        </label>
        <label>
          What do you need?
          <select value={form.project_type} onChange={set("project_type")} data-testid={`${idPrefix}-project-type-select`}>
            {PROJECT_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </label>
      </div>
      <label>
        Budget range
        <select value={form.budget} onChange={set("budget")} data-testid={`${idPrefix}-budget-select`}>
          <option value="">Prefer not to say</option>
          {BUDGETS.map((b) => (
            <option key={b}>{b}</option>
          ))}
        </select>
      </label>
      <label>
        Tell us a little about it
        <textarea required rows="3" value={form.message} onChange={set("message")} data-testid={`${idPrefix}-message-input`} />
      </label>
      <button className="primary-button" type="submit" disabled={sending} data-testid={`${idPrefix}-submit-button`}>
        {sending ? "Sending…" : "Send project brief"} <ArrowUpRight size={17} />
      </button>
    </form>
  );
}

export function InquiryProvider({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <InquiryContext.Provider value={() => setOpen(true)}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="modal-backdrop"
            data-testid="inquiry-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              className="inquiry-modal"
              initial={{ opacity: 0, y: 44, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={() => setOpen(false)} data-testid="inquiry-modal-close" aria-label="Close inquiry form">
                <X />
              </button>
              <p className="eyebrow">Start a conversation</p>
              <h2>
                Tell us what&apos;s
                <br />
                <em>on your mind.</em>
              </h2>
              <InquiryForm idPrefix="inquiry" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </InquiryContext.Provider>
  );
}
